import { useState } from 'react';
import styled from 'styled-components';
import { apiErrorText, useSetFieldReportWeatherMutation } from '@shared/api';
import {
  REPORT_WEATHER_MAX,
  reportWeatherFromVoice,
  reportWeatherInputError,
  reportWeatherText,
  type FieldDailyReport,
} from '@entities/field';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const Wrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13.5px;
  line-height: 1.6;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Value = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Form = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  max-width: 720px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const WeatherInput = styled(Input)`
  flex: 1 1 320px;
  width: auto;
`;

const Hint = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

/**
 * «Погода, условия» сменного рапорта — графа 3 ОЖР «Условия производства работ»
 * (КW1). Погоду вносит прораб в форме рапорта или называет в диктовке (её
 * записывает разбор L-2 — пометка «из диктовки»). `POST /daily-reports/{id}/weather`,
 * пустое поле — погода не указана. Прораб (писатель площадки) правит её, пока
 * рапорт у него (черновик или возврат на доработку); проверяющий ПТО — в любом
 * статусе (КR8): у сданного и принятого рапорта графу 3 иначе не заполнить
 * никому, а сервер переносит погоду и в строки ОЖР смены.
 */
export function ReportWeather({
  report,
  canEdit,
  reviewer = false,
}: {
  report: FieldDailyReport;
  /** Можно править ([`canEditReportWeather`]): проверяющий — всегда, прораб — пока рапорт у него. */
  canEdit: boolean;
  /** Смотрит проверяющий ПТО площадки. */
  reviewer?: boolean;
}) {
  const text = reportWeatherText(report.weather);
  const fromVoice = reportWeatherFromVoice(report.weather);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [save, { isLoading: saving }] = useSetFieldReportWeatherMutation();
  // Рапорт сдали, пока форма была открыта (обновление после ответа) — форму прячем.
  const open = canEdit && editing;
  const inputError = open ? reportWeatherInputError(draft) : null;
  // Рапорт уже не у прораба: погоду здесь может указать только проверяющий ПТО.
  const sealed = report.status !== 'draft' && report.status !== 'returned';

  function startEdit() {
    setDraft(text ?? '');
    setError(null);
    setEditing(true);
  }

  async function submit() {
    if (inputError) return;
    setError(null);
    try {
      await save({ id: report.id, weather: draft.trim() }).unwrap();
      setEditing(false);
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось сохранить погоду смены.'));
    }
  }

  return (
    <Wrap>
      <Row>
        <Label>Погода, условия:</Label>
        {text ? <Value>{text}</Value> : <Label>не указаны</Label>}
        {fromVoice && (
          <Badge
            $tone="info"
            title="Погоду назвал прораб в диктовке, записана как распознана — сверьте"
          >
            из диктовки
          </Badge>
        )}
        {canEdit && !editing && (
          <Button type="button" $variant="ghost" $size="sm" onClick={startEdit}>
            {text ? 'Изменить' : 'Указать…'}
          </Button>
        )}
      </Row>
      {!text && !open && (
        <Hint>
          Пока погода не указана, графа 3 ОЖР «Условия производства работ» по смене пустая.
          {!canEdit && sealed && ' Указать погоду сданного рапорта может проверяющий ПТО.'}
        </Hint>
      )}
      {open && (
        <>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <WeatherInput
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Например: ясно, −5 °C, ветер северный 3 м/с"
              aria-label="Погода, условия смены"
              aria-invalid={!!inputError}
              autoFocus
            />
            <Button type="submit" $size="sm" disabled={saving || !!inputError}>
              {saving ? 'Сохраняю…' : draft.trim() ? 'Сохранить' : 'Очистить'}
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Отмена
            </Button>
          </Form>
          <Hint $error={!!inputError}>
            {inputError ??
              `Коротко, как в журнале: небо, температура, ветер, осадки — до ${REPORT_WEATHER_MAX} символов. Пустое поле — погода не указана.`}
          </Hint>
          {reviewer && sealed && (
            <Hint>
              Рапорт уже сдан — погоду записывает проверяющий ПТО; она попадёт и в графу 3 строк ОЖР
              этой смены.
            </Hint>
          )}
        </>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
    </Wrap>
  );
}
